/**
 * Presentation logic for the Mystic draft screen.
 *
 * Split out of the component so it can be tested without mounting a browser,
 * the same way reading-intent.js and reading-exit.js are.
 */

// A verb per aspect, so the chart reads as a description of a person rather
// than a table of angles.
const ASPECT_VERBS = {
  conjunction: 'sits on',
  opposition: 'faces',
  square: 'presses against',
  trine: 'flows with',
  sextile: 'supports',
}

const PACE_WORDS = {
  quick: 'briskly',
  balanced: 'evenly',
  patient: 'slowly',
}

export function aspectVerb (aspect) {
  return ASPECT_VERBS[aspect] || 'meets'
}

// Chart points in plain words (e2e 2026-10-30, S39: "my Ascendant flows with
// your Mars" was jargon). Planets keep their names.
const PLAIN_POINTS = {
  ascendant: 'rising sign',
  descendant: 'setting sign',
  midheaven: 'highest point',
  mc: 'highest point',
  ic: 'lowest point',
  imum_coeli: 'lowest point',
  north_node: 'north node',
  south_node: 'south node',
}

export function plainPoint (name) {
  const key = String(name || '').toLowerCase().replace(/[\s-]+/g, '_')
  return PLAIN_POINTS[key] || capitalise(String(name || ''))
}

export function capitalise (word) {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : ''
}

/**
 * Gold hairline when the chart decided a field, lavender when the seeker did.
 * Anything unlabelled is the chart, because that is the state everything starts
 * in — the seeker has not touched it yet.
 */
export function provenanceClass (field) {
  return field && field.source === 'seeker' ? 'from-seeker' : 'from-chart'
}

/**
 * The repaint button says what pressing it will cost, including when the answer
 * is "you cannot afford this" — a disabled button that does not say why is a
 * dead end.
 */
export function repaintLabel (portrait, painting) {
  if (painting) return 'Painting…'
  if (!portrait) return 'Repaint'
  if (portrait.free_remaining > 0) return `Repaint · ${portrait.free_remaining} free`
  return portrait.can_afford
    ? `Repaint · ${portrait.cost} carats`
    : `Repaint · ${portrait.cost} carats needed`
}

/** When and where the chart was cast. Empty rather than "Invalid Date". */
export function castLine (bornWith) {
  if (!bornWith || !bornWith.born_at) return ''
  const when = new Date(bornWith.born_at)
  if (Number.isNaN(when.getTime())) return ''
  const stamp = when.toLocaleString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
  return bornWith.place ? `Cast ${stamp} · ${bornWith.place}` : `Cast ${stamp}`
}

export function paceWord (pace) {
  return PACE_WORDS[pace] || 'evenly'
}


/**
 * A chart line, meaning first. The backend composes one sentence per line in
 * the Mystic's voice ("My drive opens a door for who I am"); when a payload
 * predates that, the technical sentence stands in — still first person, still
 * a sentence, never a bare "its Mars supports its Sun".
 */
export function meaningLine (line, between = false) {
  if (!line) return ''
  if (line.meaning) return line.meaning
  const a = capitalise(line.from)
  const b = capitalise(line.to)
  return `My ${a} ${aspectVerb(line.aspect)} ${between ? 'your' : 'my'} ${b}.`
}

/**
 * The chart sheet takes one shape. The draft carries the chart as
 * `born_with` + `synastry` + `bond`; the Studio's /my-chart/ already IS the
 * shape. Same component either way.
 */
export function sheetFromDraft (payload) {
  if (!payload || !payload.born_with) return payload
  const b = payload.born_with
  return {
    sun: b.sun, moon: b.moon, rising: b.rising,
    placements: b.placements || {},
    aspects: b.aspects || [],
    contacts: (payload.synastry && payload.synastry.contacts) || [],
    born: { at: b.born_at, local: b.born_local, place: b.place, precision: b.place_precision },
    bond: (payload.bond && payload.bond.value) || '',
  }
}


/**
 * "on September 14 at 9:33 AM" — the birth moment as a clause, on the
 * BIRTHPLACE's clock. `local` is naive ISO (no zone) straight from the chart;
 * it is parsed field by field so the browser never converts it. `at` (a UTC
 * instant) is only a fallback for payloads that predate `local`.
 */
export function bornWhen ({ local, at } = {}) {
  let d = null
  if (local) {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local)
    if (m) d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  }
  if (!d && at) d = new Date(at)
  if (!d || isNaN(d.getTime())) return ''
  const day = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return `on ${day} at ${time}`
}
